import { Box, Container, Text, useColorModeValue } from '@chakra-ui/react'
import { Routes, Route, Link, useNavigate } from 'react-router-dom'
import Home from './pages/Home'
import Login from './pages/Login'
import { UserForm } from './pages/UserForm'
import { SubmissionSummary } from './pages/SubmissionSummary'
import { ConsultaExpediente } from './pages/ConsultaExpediente'
import DetalleExpediente from './pages/DetalleExpediente'
import DirectExpediente from './pages/DirectExpediente'
import { AdminUserForm } from './pages/AdminUserForm'
import { AdminRoute } from './components/AdminRoute'
import { Dashboard } from './pages/Dashboard'
import DirectDashboard from './pages/DirectDashboard'
import { AdminUsers } from './pages/AdminUsers'
import ConvexConfig from './pages/ConvexConfig'
import TestConvex from './pages/TestConvex'
import { UltimasActualizaciones } from './pages/UltimasActualizaciones'
import Documentos from './pages/Documentos'
import EstadisticasActividad from './pages/EstadisticasActividad'
import ResetPassword from './pages/ResetPassword'
import { AuthProvider } from './components/AuthProvider'
import { ProtectedRoute } from './components/ProtectedRoute'
import { useEffect } from 'react'

// Componente para manejar redirecciones después del login
// Se mantiene por compatibilidad pero ya no se usa directamente
// ya que el login redirige directamente al dashboard
const LoginRedirect = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirigir siempre al dashboard
    navigate('/dashboard', { replace: true });
  }, [navigate]);
  
  return null;
};

function App() {
  // Rastrear actividad del usuario para mejorar la persistencia de sesión
  useEffect(() => {
    const updateLastActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
    };

    // Eventos que indican actividad del usuario
    const activityEvents = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle para evitar demasiadas actualizaciones
    let lastUpdate = 0;
    const throttledUpdate = () => {
      const now = Date.now();
      if (now - lastUpdate > 30000) { // Actualizar máximo cada 30 segundos
        updateLastActivity();
        lastUpdate = now;
      }
    };

    // Agregar listeners de actividad
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledUpdate, true);
    });

    // Actualizar actividad inicial
    updateLastActivity();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledUpdate, true);
      });
    };
  }, []);

  // Detectar navegación (refresh, botón atrás, etc.) para evitar expulsión de sesión
  useEffect(() => {
    // Marcar que estamos navegando cuando la página se carga
    sessionStorage.setItem('isNavigating', 'true');
    
    // Después de 2 segundos, marcar que ya no estamos navegando
    const navigationTimer = setTimeout(() => {
      sessionStorage.setItem('isNavigating', 'false');
    }, 2000);

    // Detectar eventos de navegación del navegador
    const handleBeforeUnload = () => {
      sessionStorage.setItem('isNavigating', 'true');
    };

    const handlePopState = () => {
      console.log('Navegación detectada (botón atrás/adelante)');
      sessionStorage.setItem('isNavigating', 'true');
      setTimeout(() => {
        sessionStorage.setItem('isNavigating', 'false');
      }, 2000);
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // Detectar si la página viene del cache (navegación con botón atrás)
      if (event.persisted) {
        console.log('Página restaurada desde cache (bfcache)');
        sessionStorage.setItem('isNavigating', 'true');
        setTimeout(() => {
          sessionStorage.setItem('isNavigating', 'false');
        }, 2000);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pageshow', handlePageShow as EventListener);

    return () => {
      clearTimeout(navigationTimer);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pageshow', handlePageShow as EventListener);
    };
  }, []);

  return (
    <AuthProvider>
      <Box minH="100vh" display="flex" flexDirection="column">
        <Box flex="1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/form" element={<UserForm />} />
            <Route path="/submission-summary" element={<SubmissionSummary />} />
            <Route path="/consulta-expediente" element={<ConsultaExpediente />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/login-success" element={<LoginRedirect />} />
            
            {/* Rutas protegidas que requieren autenticación */}
            {/* Ruta normal con ProtectedRoute (que está causando conflictos) */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Rutas alternativas directas para solucionar problemas de redirección */}
            <Route 
              path="/direct-dashboard" 
              element={<DirectDashboard />} 
            />
            {/* Ruta protegida original (puede causar problemas) */}
            <Route 
              path="/expediente/:id" 
              element={
                <ProtectedRoute>
                  <DetalleExpediente />
                </ProtectedRoute>
              } 
            />
            {/* Ruta directa nueva para expedientes sin problemas de redirección */}
            <Route 
              path="/direct-expediente/:id" 
              element={<DirectExpediente />} 
            />
            <Route 
              path="/ultimas-actualizaciones" 
              element={
                <ProtectedRoute>
                  <UltimasActualizaciones />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/documentos" 
              element={
                <ProtectedRoute>
                  <Documentos />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/estadisticas-actividad" 
              element={
                <ProtectedRoute>
                  <EstadisticasActividad />
                </ProtectedRoute>
              } 
            />
            
            {/* Rutas de administrador */}
            <Route 
              path="/admin/users" 
              element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              }
            />
            <Route 
              path="/admin/users/new" 
              element={
                <AdminRoute>
                  <AdminUserForm />
                </AdminRoute>
              } 
            />
            <Route
              path="/convex-config"
              element={
                <AdminRoute>
                  <ConvexConfig />
                </AdminRoute>
              }
            />
            <Route
              path="/test-convex"
              element={
                <AdminRoute>
                  <TestConvex />
                </AdminRoute>
              }
            />
          </Routes>
        </Box>
        
        <Box 
          as="footer" 
          py={4} 
          textAlign="center"
          fontSize="sm"
          color={useColorModeValue('gray.600', 'gray.400')}
        >
          <Container maxW="container.xl">
            <Text>
              © Copyright. Desarrollado por <a href="https://www.ciudalia.com" target="_blank" rel="noopener noreferrer">Ciudalia</a> para FAMMA. Todos los derechos reservados.{' '}
              <Box 
                as={Link}
                to="/login"
                color="brand.500"
                _hover={{ textDecoration: 'underline' }}
              >
                Acceso técnico
              </Box>
            </Text>
          </Container>
        </Box>
      </Box>
    </AuthProvider>
  )
}

export default App
